/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import './SearchResultCard.scss';

import * as React from 'react';
import cx from 'classnames';

import { Icon, FauxUnavailableImage } from 'sarsaparilla';

import { FavoriteHeart } from 'shared-ui/favorites';

import type { SearchResultItem } from '../../api/search.types';
import { CardInventoryTag, CardLocation, CardFooter } from './SearchResultCard.utils';
import { SearchResultCardLinkHeading } from './components/SearchResultCardLinkHeading';

interface SearchResultCardProps extends React.HTMLAttributes<HTMLDivElement> {
    item: SearchResultItem;
    isActive?: boolean;
    hasCloseButton?: boolean;
    onCloseClick?: () => void;
    onHover?: (item: SearchResultItem) => void;
    onHoverLeave?: (item: SearchResultItem) => void;
    shouldFocusOnMount?: boolean;
}

export function SearchResultCard({
    item,
    isActive,
    hasCloseButton,
    onCloseClick,
    onHover,
    onHoverLeave,
    className,
    shouldFocusOnMount,
    ...rest
}: SearchResultCardProps) {
    const headingId = React.useId();
    const cardDivRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        // Focus the card on mount if it has a close button.
        if (shouldFocusOnMount && cardDivRef.current) {
            window.setTimeout(() => {
                cardDivRef.current?.focus();
            }, 200);
        }
    }, [shouldFocusOnMount]);

    // Bail out if no item is provided
    if (!item) return null;

    const onHoverOrFocus = () => {
        if (onHover) onHover(item);
    };

    const onHoverLeaveOrBlur = () => {
        if (onHoverLeave) onHoverLeave(item);
    };

    return (
        <div
            ref={cardDivRef}
            className="search--search-result-card"
            onMouseEnter={onHoverOrFocus}
            onMouseLeave={onHoverLeaveOrBlur}
            onFocus={onHoverOrFocus}
            onBlur={onHoverLeaveOrBlur}
            tabIndex={shouldFocusOnMount ? -1 : undefined}
            role={shouldFocusOnMount ? 'dialog' : undefined}
            aria-labelledby={headingId}
            {...rest}
        >
            <div
                className={cx('card-inner', {
                    'is-active': isActive,
                })}
            >
                <div className="image-wrap">
                    {item.preview_image_url ? (
                        <img
                            src={item.preview_image_url}
                            alt={item.name}
                            className="img"
                        />
                    ) : (
                        <FauxUnavailableImage className="img" />
                    )}

                    <div className="image-buttons-group">
                        <div className="left">{/* Badge here later */}</div>

                        <div className="right">
                            <FavoriteHeart
                                entityId={item.entity_id}
                                entityType={item.entity_type}
                                parentId={item.parent_id}
                                parentType={item.parent_type}
                                favAriaLabel={item.name}
                                showFavoriteText={false}
                                editable
                            />

                            {hasCloseButton ? (
                                <button
                                    type="button"
                                    className="close-button"
                                    onClick={onCloseClick}
                                    aria-label={`Close ${item.name} card`}
                                >
                                    <Icon iconName="close" className="close-icon" />
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className="content-wrap">
                    <div className="card-top-content">
                        <CardInventoryTag item={item} />

                        <SearchResultCardLinkHeading item={item} id={headingId} />

                        <CardLocation
                            parent={item.parent_name}
                            distance={item.distance}
                            city={item.city}
                            state={item.state_code}
                        />
                    </div>

                    <CardFooter item={item} />
                </div>
            </div>
        </div>
    );
}
